import { Component, DestroyRef, Input, EventEmitter, Output, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { SearchInputComponent } from '../../../inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../inputs/sort-dropdown/sort-dropdown.component';
import { SelectExistingRuleTableComponent } from '../select-existing-rule-table/select-existing-rule-table.component';
import { PaginationComponent } from '../../../inputs/pagination/pagination.component';
import { GroupAudienceService } from '../../../../services/segments/group-audience.service';
import { IPagination } from '../../../../types/contactModule';
import { IOrganizationSort } from '../../../../pages/organization/contacts/organization-list/organization-list.component';
import { IEntitySegmentQuery } from '../segment-utilities';

/**
 * Pick a saved segment to copy its conditions from. Keeps its own list, so
 * it never changes the segments page behind it.
 */
@Component({
  selector: 'select-existing-rule-modal',
  standalone: true,
  imports: [ModalComponent, SearchInputComponent, SortDropdownComponent, SelectExistingRuleTableComponent, PaginationComponent],
  templateUrl: './select-existing-rule-modal.component.html',
  styleUrl: './select-existing-rule-modal.component.css',
})
export class SelectExistingRuleModalComponent implements OnInit {
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) opeModal!: boolean;
  @Output() closeModal = new EventEmitter();

  readonly titleId = 'seg-copy-title';

  segments: IEntitySegmentQuery[] = [];
  loading = true;
  loadError = '';
  private loadSub?: Subscription;

  searchTerm: string = '';
  sortCriteria: IOrganizationSort = 'name,asc';
  pageIndex = 0;
  /** One of the pager's own sizes (10, 15, 20…), so its range and select agree with the rows. */
  pageSize = 10;

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  tagsPagination: IPagination = {
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };

  load() {
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    const params: Record<string, any> = { page: this.pageIndex, size: this.pageSize, sort: this.sortCriteria };
    const term = this.searchTerm.trim();
    if (term) params['name.contains'] = term;
    this.loadSub = this.audience
      .listSegments(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.segments = page.items;
          this.tagsPagination = { ...page.itemPagination, pageNumber: this.pageIndex };
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Segments did not load. Check your connection and try again.';
        },
      });
  }

  goToPreviousPage() {
    this.updateTagPage(this.pageIndex - 1);
  }
  goToNextPage() {
    this.updateTagPage(this.pageIndex + 1);
  }
  updateTagPage(newPage: number) {
    this.pageIndex = Math.max(0, newPage);
    this.load();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    this.pageIndex = 0;
    this.load();
  }

  updateSortCriteria(criteria: IOrganizationSort) {
    this.sortCriteria = criteria;
    this.pageIndex = 0;
    this.load();
  }

  close() {
    this.closeModal.emit();
  }

  ngOnInit() {
    this.load();
  }
}
